import React, { useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { motion } from 'framer-motion';
import { SectionHeadingH2 } from "components/misc/Headings.js";
import { Container as ContainerBase, ContentWithPaddingXl as ContentBase } from "components/misc/Layouts.js";
import { ReactComponent as CheckboxIcon } from "images/checkbox-circle.svg";
import TooltipTrigger from 'react-popper-tooltip';
import { Link } from 'react-router-dom';
import 'react-popper-tooltip/dist/styles.css';
import Content from "_content/Content.js";
import { isNil } from 'lodash-es';

const Container = styled(ContainerBase)`
  ${tw`min-h-screen text-gray-100 -ml-8 -mr-8 mb-24`}
  background: linear-gradient(180deg, rgb(22, 24, 29) 0%, rgb(26, 32, 44) 100%);
`;

const TabsContainer = styled.div`
  ${tw`flex justify-center mb-16 mx-auto max-w-2xl`}
  background: rgba(45, 48, 55, 0.8);
  border-radius: 100px;
  padding: 4px;
  
  @media (max-width: 640px) {
    ${tw`flex-col gap-2`}
    border-radius: 20px;
  }
`;

const TabButton = styled.button`
  ${tw`px-8 py-3 text-sm font-medium rounded-full transition-all duration-300 flex items-center justify-center gap-2 focus:outline-none active:outline-none`}
  ${props => props.active ? tw`bg-gray-700` : tw`text-gray-400 hover:text-white hover:bg-gray-700`}

  @media (max-width: 640px) {
    ${tw`w-full rounded-xl py-2`}
    flex-direction: column;
    gap: 4px;
  }
`;

const Tag = styled.span`
  ${props => props.type === 'discount' ?
    css`
      ${tw`bg-yellow-500 text-black text-xs px-3 py-1 rounded-full font-medium`}
      box-shadow: 0 2px 4px rgba(234, 179, 8, 0.3);
    ` :
    css`
      ${tw`bg-green-500 text-white text-xs px-3 py-1 rounded-full font-medium`}
      box-shadow: 0 2px 4px rgba(22, 163, 74, 0.3);
    `
  }
  
  @media (max-width: 640px) {
    ${tw`text-xxs px-2 py-0`}
  }
`;

const PlansGrid = styled(motion.div)`
  ${tw`grid gap-8 lg:grid-cols-3 max-w-6xl mx-auto px-4`}
`;

const PlanCard = styled.div`
  ${tw`bg-white rounded-3xl p-8 flex flex-col min-h-144 transform transition-all duration-300`}
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  &:hover {
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
  ${props => props.featured && css`
    ${tw`border-2 border-green-500`}
    transform: scale(1.02);
  `}
  .featuredTextPromo {
    ${tw`text-xxs font-bold px-3 rounded py-2 uppercase bg-green-300 text-green-900 leading-none mt-1 sm:mt-0 sm:w-auto text-center`}
  }

`;

const PlanPrice = styled.div`
  ${tw`text-gray-400 text-xl mb-2 flex items-baseline`}
  .amount {
    ${tw`text-gray-800 font-bold`}
  }
      .old-price {
    ${tw`text-red-500 font-extrabold line-through pr-2`}
  }
`;

const PlanDescription = tw.p`text-gray-800 text-sm mb-4 mt-4`;

const FeaturesList = styled.ul`
  ${tw`space-y-4 flex-grow`}
`;

const Feature = styled.li`
  ${tw`flex items-center gap-3 text-gray-800`}
  svg {
    ${tw`w-5 h-5 text-green-500 transition-transform duration-200`}
  }
  &:hover svg {
    transform: scale(1.1);
  }
`;

const ContentWithPaddingXl = styled(ContentBase)`
  ${tw`relative z-10 mx-auto px-4 py-16 sm:py-24`}
`;

const HeaderContainer = tw.div`text-center`;
const Heading = styled(SectionHeadingH2)`
  ${tw`text-4xl font-bold text-white mb-12`}
`;

const PlanHeader = styled.div`
  ${tw`text-left mb-4`}
`;
const PlanName = tw.h3`text-2xl font-bold mb-2 text-gray-800`;

const ActionButton = styled.a`
  ${tw`w-full py-4 px-6 rounded-lg font-medium transition-all duration-300 mb-8 text-center`}
  ${props => props.primary ?
    css`
      background: linear-gradient(to right, #00b894, #00cec9);
      color: #f3f4f6;
      box-shadow: 0 4px 6px rgba(0, 184, 148, 0.3);
      &:hover {
        background: linear-gradient(to right, #00a08b, #00b2b2);
        transform: translateY(-2px);
        box-shadow: 0 6px 8px rgba(0, 184, 148, 0.4);
      }
    ` :
    css`
      background: linear-gradient(to right, #475569, #718096);
      color: #f3f4f6;
      box-shadow: 0 4px 6px rgba(71, 85, 105, 0.3);
      &:hover {
        background: linear-gradient(to right, #334155, #475569);
        transform: translateY(-2px);
        box-shadow: 0 6px 8px rgba(71, 85, 105, 0.4);
      }
    `
  }
`;


const Tooltip = ({ children, tooltip, hideArrow, ...props }) => (
  <TooltipTrigger
    {...props}
    tooltip={({ arrowRef, tooltipRef, getArrowProps, getTooltipProps, placement }) => (
      <div {...getTooltipProps({ ref: tooltipRef, className: 'tooltip-container display-linebreak' })}>
        {!hideArrow && <div {...getArrowProps({ ref: arrowRef, className: 'tooltip-arrow', 'data-placement': placement })} />}
        {tooltip}
      </div>
    )}
  >
    {({ getTriggerProps, triggerRef }) => (
      <span {...getTriggerProps({ ref: triggerRef, className: 'trigger' })}>
        {children}
      </span>
    )}
  </TooltipTrigger>
);

export default ({ translations, language, newInviteCode }) => {
  const { heading, tabs } = translations.pricingModule;
  let plans = Content.plans;
  const [activeTab, setActiveTab] = useState(Object.keys(tabs)[2]);
  if (language === 'es' && translations.pageInfo.locationString.includes('/es/')) {
    plans = Content.esPlans;
  }

  const tabsKeys = Object.keys(translations.pricingModule.tabs);

  const inviteCode = newInviteCode !== "" ? newInviteCode : localStorage.getItem('inviteCode');
  const timestamp = parseInt(localStorage.getItem('inviteCodeTimestamp'), 10);
  const isRecent = !isNaN(timestamp) && (Date.now() - timestamp <= 1000 * 60 * 60 * 24 * 7); // 7 days

  const appendInviteParam = (url) => {
    if (inviteCode && isRecent) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}inviteRef=${inviteCode}`;
    }
    return url;
  };

  return (
    <Container>
      <ContentWithPaddingXl>
        <HeaderContainer>
          <Heading>{heading}</Heading>
          {/* 
            <TabsContainer>
            {Object.keys(tabs).map((tab, index) => (
              <TabButton key={index} active={activeTab === tab} onClick={() => setActiveTab(tab)}>
                {tab}
                {index === 1 && <Tag type="discount">-50%</Tag>}
                {index > 1 && <Tag>Best value</Tag>}
              </TabButton>
            ))}
          </TabsContainer>
          */}
          
        </HeaderContainer>

        <PlansGrid
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {plans[plans[activeTab] ? activeTab : tabsKeys[2]].map((plan, index) => (
            <PlanCard key={index} featured={plan.featured}>
              <PlanHeader>
                <PlanName>Gunbot {plan.name}</PlanName>
              </PlanHeader>
              <PlanPrice>
                {plan.oldPrice && <span className="old-price">{plan.oldPrice} </span>}
                <span className="amount">{plan.price[0]}</span>
              </PlanPrice>
              <div>
                {plan.featuredPromo ? <span className="featuredTextPromo">{plan.featuredPromo}</span> : <span className="featuredTextDummy"></span>}
              </div>
              <PlanDescription>{plan.description}</PlanDescription>
              <ActionButton href={appendInviteParam(plan.url.directLink)}>
                Get Gunbot {plan.name}
              </ActionButton>
              <FeaturesList>
                {plan.features.map((feature, featureIndex) => (
                  <Feature key={featureIndex}>
                    <CheckboxIcon />
                    <Tooltip placement="top" trigger="hover" tooltip={feature[1]}>
                      <span>{feature[0]}</span>
                    </Tooltip>
                  </Feature>
                ))}
                {plan.url.details && (
                  <p tw="text-center mt-8 font-bold text-gray-800">
                    <Link to={plan.url.details}>More details about Gunbot {plan.name}</Link>
                  </p>
                )}
              </FeaturesList>
            </PlanCard>
          ))}
        </PlansGrid>
      </ContentWithPaddingXl>
    </Container>
  );
};
